import React, { useEffect, useMemo, useState } from "react";
import { Button, Drawer, Input, Select, Tooltip } from "antd";
import { t } from "i18next";
import moment from "moment";
import { PhoneField, formatPhone, stripPhone } from "./fields";
import { genderVars, validateTCKN } from "./helpers";
import ConsentPanel from "./ConsentPanel";

const { TextArea } = Input;

/**
 * Hasta Kartı — sayfadan çıkmadan açılan drawer. Kimlik bloğu cinsiyet renginde çerçevelenir
 * (eski ekrandaki renklendirmenin aynısı) ve cinsiyet değişince renk anında döner.
 */
export default function PatientCardDrawer({ open, patient, features, onClose, onSave, saving, readOnly }) {
    const [form, setForm] = useState({});
    const [dirty, setDirty] = useState(false);

    const baseline = useMemo(() => {
        if (!patient) return {};
        const birthdate = patient.birthYear
            ? moment({ year: patient.birthYear, month: (patient.birthMonth || 1) - 1, day: patient.birthDay || 1 })
            : null;
        return {
            customId: patient.customId ?? '',
            otherPatientId: patient.otherPatientId ?? '',
            name: patient.name ?? '',
            lastname: patient.lastname ?? '',
            motherName: patient.motherName ?? '',
            fatherName: patient.fatherName ?? '',
            birthdate: birthdate && birthdate.isValid() ? birthdate.format('YYYY-MM-DD') : '',
            gender: patient.gender ?? null,
            phone1: { code: patient.phoneCode1 || 90, phone: formatPhone(patient.phone1) },
            phone2: { code: patient.phoneCode2 || 90, phone: formatPhone(patient.phone2) },
            email: patient.email ?? '',
            note: patient.note ?? '',
            address: patient.address ?? '',
            weight: patient.weight ?? null,
            height: patient.height ?? null,
        };
    }, [patient]);

    useEffect(() => {
        setForm(baseline);
        setDirty(false);
    }, [baseline, open]);

    const set = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
        setDirty(true);
    };

    // Ad/soyad gibi alanlar eski ekranda da büyük harfe çevriliyor; aynı davranış korunur.
    // Boş alan null gider: '' göndermek her kayıtta sahte bir null→'' değişikliği üretiyordu.
    const upper = (v) => {
        const trimmed = (v ?? '').trim();
        return trimmed ? trimmed.toLocaleUpperCase('tr-TR') : null;
    };

    const handleSave = async () => {
        const b = form.birthdate ? moment(form.birthdate, 'YYYY-MM-DD') : null;
        const payload = {
            customId: upper(form.customId),
            otherPatientId: upper(form.otherPatientId),
            name: upper(form.name),
            lastname: upper(form.lastname),
            motherName: upper(form.motherName),
            fatherName: upper(form.fatherName),
            birthYear: b && b.isValid() ? b.year() : null,
            birthMonth: b && b.isValid() ? b.month() + 1 : null,
            birthDay: b && b.isValid() ? b.date() : null,
            gender: form.gender || null,
            phoneCode1: form.phone1?.code ?? 90,
            phone1: stripPhone(form.phone1?.phone) || null,
            phoneCode2: form.phone2?.code ?? 90,
            phone2: stripPhone(form.phone2?.phone) || null,
            email: form.email || null,
            note: upper(form.note),
            address: upper(form.address),
            weight: form.weight ? Number(form.weight) : null,
            height: form.height ? Number(form.height) : null,
        };
        const ok = await onSave(payload);
        if (ok) setDirty(false);
    };

    const customId = (form.customId || '').trim();
    const tcknValid = validateTCKN(customId);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            width={560}
            rootClassName="ws-root"
            title={t('Patient Card')}
            maskClosable={!dirty}
            footer={(
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <span className={`hint${dirty ? ' warn' : ''}`}>
                        {dirty ? t('Unsaved changes') : t('No unsaved changes')}
                    </span>
                    <span style={{ flex: 1 }} />
                    <Button size="small" onClick={onClose}>{t('Close')}</Button>
                    <Button
                        size="small"
                        type="primary"
                        disabled={!dirty || readOnly}
                        loading={saving === 'patient'}
                        onClick={handleSave}
                    >
                        {t('Save Patient')}
                    </Button>
                </div>
            )}
        >
            <div
                style={{
                    ...genderVars(form.gender),
                    border: '1.5px solid var(--ws-g)',
                    background: 'var(--ws-g-bg)',
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 14,
                }}
            >
                <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))' }}>
                    <div className="f">
                        <span className="lbl">{t('Identity')}</span>
                        <Input
                            className="ws-mono"
                            inputMode="numeric"
                            disabled={readOnly}
                            value={form.customId ?? ''}
                            onChange={e => set('customId', e.target.value)}
                        />
                        {customId.length > 0 && (
                            <span className={`hint ${tcknValid ? 'ok' : 'warn'}`}>
                                {tcknValid
                                    ? '✓ T.C. kimlik algoritmasına uygun'
                                    : t('The patient information you entered does not comply with T.C. identity number rules.')}
                            </span>
                        )}
                    </div>
                    <div className="f">
                        <span className="lbl">{t('Sex')}</span>
                        <Select
                            disabled={readOnly}
                            value={form.gender ?? undefined}
                            onChange={v => set('gender', v)}
                            options={[
                                { value: 'M', label: t('Male') },
                                { value: 'F', label: t('Female') },
                                { value: 'O', label: t('Other') },
                            ]}
                        />
                    </div>
                    <div className="f">
                        <span className="lbl">{t('Name')}</span>
                        <Input disabled={readOnly} value={form.name ?? ''} onChange={e => set('name', e.target.value)} />
                    </div>
                    <div className="f">
                        <span className="lbl">{t('Lastname')}</span>
                        <Input disabled={readOnly} value={form.lastname ?? ''} onChange={e => set('lastname', e.target.value)} />
                    </div>
                    <div className="f">
                        <span className="lbl">{t('Birthdate')}</span>
                        <Input
                            type="date"
                            className="ws-mono"
                            disabled={readOnly}
                            value={form.birthdate ?? ''}
                            onChange={e => set('birthdate', e.target.value)}
                        />
                    </div>
                    <div className="f">
                        <span className="lbl">{t('Other Id')}</span>
                        <Input
                            className="ws-mono"
                            disabled={readOnly}
                            value={form.otherPatientId ?? ''}
                            onChange={e => set('otherPatientId', e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))' }}>
                <div className="f">
                    <span className="lbl">{t('Phone')} 1</span>
                    <PhoneField value={form.phone1} onChange={v => set('phone1', v)} disabled={readOnly} />
                </div>
                <div className="f">
                    <span className="lbl">{t('Phone')} 2</span>
                    <PhoneField value={form.phone2} onChange={v => set('phone2', v)} disabled={readOnly} />
                </div>
                <div className="f" style={{ gridColumn: 'span 2' }}>
                    <span className="lbl">{t('Email')}</span>
                    <Input
                        type="email"
                        disabled={readOnly}
                        value={form.email ?? ''}
                        onChange={e => set('email', e.target.value)}
                    />
                </div>
                <div className="f">
                    <span className="lbl">{t('Mother Name')}</span>
                    <Input disabled={readOnly} value={form.motherName ?? ''} onChange={e => set('motherName', e.target.value)} />
                </div>
                <div className="f">
                    <span className="lbl">{t('Father Name')}</span>
                    <Input disabled={readOnly} value={form.fatherName ?? ''} onChange={e => set('fatherName', e.target.value)} />
                </div>
                <div className="f">
                    <span className="lbl">{t('Height')}</span>
                    <Input
                        className="ws-mono"
                        inputMode="numeric"
                        disabled={readOnly}
                        value={form.height ?? ''}
                        onChange={e => set('height', e.target.value.replace(/\D/g, ''))}
                    />
                </div>
                <div className="f">
                    <span className="lbl">{t('Weight')}</span>
                    <Input
                        className="ws-mono"
                        inputMode="numeric"
                        disabled={readOnly}
                        value={form.weight ?? ''}
                        onChange={e => set('weight', e.target.value.replace(/\D/g, ''))}
                    />
                </div>
                <div className="f" style={{ gridColumn: 'span 2' }}>
                    <span className="lbl">{t('Address')}</span>
                    <TextArea rows={2} disabled={readOnly} value={form.address ?? ''} onChange={e => set('address', e.target.value)} />
                </div>
                <div className="f" style={{ gridColumn: 'span 2' }}>
                    <span className="lbl">
                        <Tooltip title={t('Shown as a warning on the patient strip.')}>
                            {t('Patient Note')}
                        </Tooltip>
                    </span>
                    <TextArea rows={2} disabled={readOnly} value={form.note ?? ''} onChange={e => set('note', e.target.value)} />
                </div>
            </div>

            <ConsentPanel
                patientId={patient?.id}
                documentType="TICARI"
                enabled={!!features?.ticariConsentEnabled}
            />
        </Drawer>
    );
}
